import React, { useEffect, useMemo, useState } from 'react';
import { SimpleUser } from '../../services/userLoader';
import { LeaveAdjustmentKind, LeaveBalanceSnapshot } from '../../types/leavePolicy';
import {
  ADJUSTMENT_KIND_LABELS,
  ADJUSTMENT_KINDS,
  LeaveBalanceService,
  isCreditKind,
  resolveAdjustmentKind,
  signedAdjustmentDays,
} from '../../services/leaveBalanceService';
import './LeaveBalanceAdjustModal.css';

/**
 * Adjust an employee's leave balance, for one leave type, in one year.
 *
 * This is the Edit action on the Leave Assignment sheet, and it deliberately records an
 * *adjustment* rather than overwriting a number. A balance is computed —
 * `granted − approved − pending + comp-off + HR adjustments` — so there is nothing to overwrite:
 * the only honest way to move it is the term that exists for the purpose. That also makes every
 * change reversible and attributable, which a direct edit would not be.
 *
 * What is deliberately *not* editable here:
 *  - The granted allocation. That belongs to the leave policy, and a per-employee override would
 *    reintroduce the divergent second source of truth that the old Assign Policy form was removed
 *    for. The dialog links to Leave Policies instead.
 *  - Employee name, ID, department, designation, manager. Those are identity fields owned by User
 *    Management (Cognito plus the UserProfiles table); the leave module has no business writing
 *    them. The dialog links there instead.
 */

export interface AdjustLeaveTypeOption {
  code: string;
  name: string;
}

interface LeaveBalanceAdjustModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: SimpleUser | null;
  year: number;
  /** The employee's balances for `year`, so the dialog can show what it is about to change. */
  snapshots: LeaveBalanceSnapshot[];
  leaveTypes: AdjustLeaveTypeOption[];
  /** Email recorded as the author of the adjustment. */
  updatedBy: string;
  /** Preselects a leave type when the action was taken from a specific column. */
  initialLeaveTypeCode?: string;
  /**
   * Called after a successful write. `reachedServer` is false when the adjustment was cached
   * locally but never reached AppSync — the caller has to be able to tell HR the difference,
   * because a local-only grant is invisible to the employee it was meant for.
   */
  onSaved: (summary: { message: string; reachedServer: boolean }) => void;
}

/** Nearest half day. Adjustments are granted in half-day units, like every other leave figure. */
const toHalf = (n: number): number => Math.round(n * 2) / 2;

const LeaveBalanceAdjustModal: React.FC<LeaveBalanceAdjustModalProps> = ({
  isOpen,
  onClose,
  employee,
  year,
  snapshots,
  leaveTypes,
  updatedBy,
  initialLeaveTypeCode,
  onSaved,
}) => {
  const [leaveTypeCode, setLeaveTypeCode] = useState('');
  /**
   * Which side of the balance the days land on.
   *
   * Empty means "not chosen yet"; the effective kind is `kind` below, which falls back to the
   * default for the sign entered. Held separately so switching the sign can re-default it without
   * losing an explicit choice the same sign still allows.
   */
  const [kindChoice, setKindChoice] = useState<LeaveAdjustmentKind | ''>('');
  // Held as a string, not a number: a controlled number input cannot represent the intermediate
  // states of typing a negative or decimal value ('-', '-0.', '2.') without fighting the caret.
  const [daysText, setDaysText] = useState('');
  const [reason, setReason] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset on each open so a previous adjustment's reason cannot be saved against another
  // employee — the dialog is reused across every row of the sheet.
  useEffect(() => {
    if (!isOpen) return;
    setLeaveTypeCode(initialLeaveTypeCode || '');
    setKindChoice('');
    setDaysText('');
    setReason('');
    setExpiresOn('');
    setError(null);
    setSaving(false);
  }, [isOpen, initialLeaveTypeCode, employee?.email]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const snapshot = useMemo(
    () => snapshots.find(s => s.leaveTypeCode.toUpperCase() === leaveTypeCode.toUpperCase()),
    [snapshots, leaveTypeCode]
  );

  /*
   * The kind in force, and the direction read off it.
   *
   * The days field is a magnitude: it used to be signed *as well as* having a kind beside it, and
   * the sign won — so entering 28 with "Entitlement reduced" chosen stored a credit, and an
   * entitlement being reduced by 28 grew by 28 instead. Two controls saying the same thing can
   * disagree; one cannot.
   */
  const kind = resolveAdjustmentKind(isCreditKind(kindChoice || 'ENTITLEMENT_CREDIT'), kindChoice);
  const isCredit = isCreditKind(kind);

  const parsedDays = Number(daysText);
  const daysValid = daysText.trim() !== '' && Number.isFinite(parsedDays) && parsedDays !== 0;
  // Signed from the kind, magnitude from the field. A stray minus is ignored, not obeyed.
  const delta = daysValid ? toHalf(signedAdjustmentDays(kind, parsedDays)) : 0;

  /*
   * A leave type with no snapshot is a real case, not an error: ML and PL grant 0 by default and
   * are given out case by case, so an employee HR has never granted them has no row at all. The
   * current balance for such a type is 0 and the adjustment is what brings it into existence.
   */
  const currentBalance = snapshot?.balance ?? 0;
  const resulting = toHalf(currentBalance + delta);

  /*
   * What the two figures beside the balance become — the reason the kind exists.
   *
   * Both negative kinds move the balance by exactly the same amount, so the resulting-balance
   * figure above cannot tell them apart. This line is the only thing on the dialog that shows the
   * difference between taking entitlement away and recording leave already taken.
   */
  const affectsEntitlement = kind === 'ENTITLEMENT_CREDIT' || kind === 'ENTITLEMENT_REDUCTION';
  const magnitude = daysValid ? Math.abs(toHalf(parsedDays)) : 0;
  const currentEntitlement = snapshot ? LeaveBalanceService.totalEntitlement(snapshot) : 0;
  const currentUsed = snapshot ? LeaveBalanceService.totalUsed(snapshot) : 0;
  const nextEntitlement = toHalf(currentEntitlement + (affectsEntitlement ? delta : 0));
  const nextUsed = toHalf(currentUsed - (affectsEntitlement ? 0 : delta));

  /*
   * Maternity and Paternity credits are withheld until the day after they are made, so a positive
   * ML/PL adjustment will not move the balance on screen today. Said before saving rather than
   * after, because "I granted it and nothing happened" is indistinguishable from a failed write.
   */
  const withheldUntilTomorrow =
    isCredit && ['ML', 'PL'].includes(leaveTypeCode.toUpperCase());

  const goesNegative = resulting < 0;

  const canSave = !!employee && !!leaveTypeCode && daysValid && reason.trim().length > 0 && !saving;

  if (!isOpen || !employee) return null;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const { ok } = await LeaveBalanceService.addAdjustment(
        employee.email,
        leaveTypeCode,
        year,
        delta,
        updatedBy,
        reason.trim(),
        // Expiry is meaningful only for a credit — "3 days expiring on the 30th" does not read
        // sensibly for a deduction, and the balance engine only applies it to positive rows.
        isCredit && expiresOn ? expiresOn : undefined,
        kind
      );
      const sign = delta > 0 ? '+' : '';
      onSaved({
        message:
          `${sign}${delta} day(s) ${leaveTypeCode} for ${employee.fullName || employee.name}` +
          ` — ${ADJUSTMENT_KIND_LABELS[kind].toLowerCase()}` +
          (withheldUntilTomorrow ? ', credited from tomorrow' : ''),
        reachedServer: ok,
      });
      onClose();
    } catch (err: any) {
      console.error('[LeaveAdjust] Save failed:', err);
      setError(err?.errors?.[0]?.message || err?.message || 'Could not save the adjustment.');
      setSaving(false);
    }
  };

  return (
    <div className="lba-backdrop" onClick={onClose} role="presentation">
      <div
        className="lba-panel"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lba-title"
      >
        <header className="lba-head">
          <div>
            <span className="lba-eyebrow">Adjust Leave Balance</span>
            <h2 className="lba-title" id="lba-title">{employee.fullName || employee.name}</h2>
            <p className="lba-sub">
              ID {employee.employeeId || '—'} · {employee.email} · Leave year {year}
            </p>
          </div>
          <button type="button" className="lba-close" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="lba-body">
          <div className="lba-grid">
            <label className="lba-field">
              <span className="lba-label">Leave Type <em>*</em></span>
              <select
                className="lba-input"
                value={leaveTypeCode}
                onChange={e => setLeaveTypeCode(e.target.value)}
              >
                <option value="">Select a leave type…</option>
                {leaveTypes.map(t => (
                  <option key={t.code} value={t.code}>{t.name} ({t.code})</option>
                ))}
              </select>
            </label>

            <label className="lba-field">
              <span className="lba-label">Days <em>*</em></span>
              <input
                className="lba-input"
                type="number"
                step="0.5"
                min="0"
                inputMode="decimal"
                placeholder="e.g. 2 or 1.5"
                value={daysText}
                onChange={e => setDaysText(e.target.value)}
              />
              {/* No sign to explain any more: how many days here, which way in Records as. */}
              <span className="lba-help">
                How many days. Half days allowed.
              </span>
            </label>

            {/*
              Always shown, and always all four.
              This is the only control that decides direction. It used to be filtered by the sign of
              the days field, which meant two controls said the same thing — and when they
              disagreed the sign won silently, so "28" with "Entitlement reduced" chosen stored a
              credit and the entitlement went up by 28 instead of down.
            */}
            <label className="lba-field">
              <span className="lba-label">Type of change <em>*</em></span>
              <select
                className="lba-input"
                value={kind}
                onChange={e => setKindChoice(e.target.value as LeaveAdjustmentKind)}
              >
                {ADJUSTMENT_KINDS.map(k => (
                  <option key={k} value={k}>{ADJUSTMENT_KIND_LABELS[k]}</option>
                ))}
              </select>
              <span className="lba-help">
                {/* States the arithmetic outright, since nothing else on the row does. */}
                {magnitude > 0 && (
                  <>
                    <strong>{isCredit ? 'Adds' : 'Takes off'} {magnitude} day(s).</strong>{' '}
                  </>
                )}
                {affectsEntitlement
                  ? 'Changes the employee’s leave allotment for this year.'
                  : 'Changes the days already taken — the allotment stays the same.'}
              </span>
              {/* Said before saving, not after. While the schema is behind, the choice is not
                  stored and the row is read by sign — so picking "Entitlement reduced" would look
                  like it worked and show up as leave taken. */}
              {!LeaveBalanceService.adjustmentKindSupported() && (
                <span className="lba-help lba-help--warn">
                  This choice cannot be saved yet — the deployed API does not store it, so the
                  adjustment will be read by its sign
                  {isCredit ? ' as extra allotment' : ' as days taken'}. Applying
                  the AppSync schema enables it.
                </span>
              )}
            </label>
          </div>

          {/* Current → adjustment → resulting, so the effect is stated before it is committed. */}
          <div className={`lba-preview${leaveTypeCode ? '' : ' lba-preview--idle'}`}>
            <div className="lba-preview-cell">
              <span className="lba-preview-label">Current balance</span>
              <span className="lba-preview-val">{leaveTypeCode ? currentBalance : '—'}</span>
            </div>
            <span className="lba-preview-arrow" aria-hidden="true">→</span>
            <div className="lba-preview-cell">
              <span className="lba-preview-label">Adjustment</span>
              <span className={`lba-preview-val${delta > 0 ? ' lba-pos' : delta < 0 ? ' lba-neg' : ''}`}>
                {daysValid ? `${delta > 0 ? '+' : ''}${delta}` : '—'}
              </span>
            </div>
            <span className="lba-preview-arrow" aria-hidden="true">→</span>
            <div className="lba-preview-cell">
              <span className="lba-preview-label">Resulting balance</span>
              <span className={`lba-preview-val lba-preview-val--result${goesNegative ? ' lba-neg' : ''}`}>
                {leaveTypeCode && daysValid ? resulting : '—'}
              </span>
            </div>
          </div>

          {snapshot && (
            <p className="lba-context">
              {/* The real used figure — pre-go-live leave lives in this very ledger as a
                  negative row, so showing `consumed` here told HR "used 0" about days HR had
                  recorded themselves. */}
              Entitlement {currentEntitlement} · used {currentUsed}
              {snapshot.pending ? ` · pending ${snapshot.pending}` : ''}
              {snapshot.hrAdjustment ? ` · existing HR adjustments ${snapshot.hrAdjustment > 0 ? '+' : ''}${snapshot.hrAdjustment}` : ''}
              {/* Which of the two moves, and to what. The resulting-balance figure above is
                  identical for both negative kinds, so without this the choice has no visible
                  consequence until after it is saved. */}
              {daysValid && (
                <>
                  {' → '}
                  <strong>
                    entitlement {nextEntitlement} · used {nextUsed}
                  </strong>
                </>
              )}
            </p>
          )}
          {leaveTypeCode && !snapshot && (
            <p className="lba-context">
              This employee has no {leaveTypeCode} balance yet — the adjustment will create one.
            </p>
          )}

          <label className="lba-field">
            <span className="lba-label">Reason <em>*</em></span>
            <textarea
              className="lba-input lba-textarea"
              rows={2}
              placeholder="Why this balance is being adjusted — kept on the audit trail."
              value={reason}
              onChange={e => setReason(e.target.value)}
            />
          </label>

          {/* Only for credits: the balance engine ignores an expiry on a deduction. */}
          {isCredit && (
            <label className="lba-field lba-field--half">
              <span className="lba-label">Expires on <span className="lba-optional">optional</span></span>
              <input
                className="lba-input"
                type="date"
                value={expiresOn}
                onChange={e => setExpiresOn(e.target.value)}
              />
              <span className="lba-help">
                Last day these days may be used. Enforced — the balance drops them afterwards.
                Leave blank for no expiry.
              </span>
            </label>
          )}

          {goesNegative && (
            <p className="lba-note lba-note--warn">
              This leaves a negative balance of {resulting} day(s). Allowed — a correction
              sometimes needs it — but worth checking the figure.
            </p>
          )}

          {withheldUntilTomorrow && (
            <p className="lba-note lba-note--info">
              {leaveTypeCode} credits are withheld until the following day, so the balance will
              still read {currentBalance} today and {resulting} tomorrow.
            </p>
          )}

          <p className="lba-note lba-note--muted">
            To change the <strong>granted allocation</strong> rather than make a one-off
            adjustment, edit the policy in <strong>Leave Policies → Leave Types &amp; Policies</strong>.
            To change the employee's <strong>name, ID, department, designation or manager</strong>,
            use <strong>User Management</strong> — those fields are owned there.
          </p>

          {error && <p className="lba-note lba-note--error">{error}</p>}
        </div>

        <footer className="lba-foot">
          <span className="lba-audit">
            Recorded against {updatedBy || 'your account'} on the leave audit trail.
          </span>
          <div className="lba-actions">
            <button type="button" className="lba-btn lba-btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="lba-btn lba-btn--primary" onClick={handleSave} disabled={!canSave}>
              {saving ? 'Saving…' : 'Save Adjustment'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LeaveBalanceAdjustModal;
